import React, { useState, useEffect } from "react";
import { attendanceService } from "../../services/attendanceService";
import { useAuth } from "../../context/AuthContext";
import "../Teams/TeamsPage.css";

// Helper: convert a JS Date or ISO string to datetime-local input value
const toDatetimeLocal = value => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  // Format: YYYY-MM-DDTHH:mm
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CreateEventModal = ({ event, onClose, onSaved }) => {
  const { user } = useAuth();
  const isEdit = !!event;

  const [title, setTitle] = useState(event?.title ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [location, setLocation] = useState(event?.location ?? "");
  const [scheduledTime, setScheduledTime] = useState(
    toDatetimeLocal(event?.scheduled_time)
  );
  const [duration, setDuration] = useState(
    event?.duration_minutes != null ? String(event.duration_minutes) : "60"
  );
  const [qrExpiry, setQrExpiry] = useState(
    toDatetimeLocal(event?.qr_expiry_time)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset form when event prop changes
  useEffect(() => {
    setTitle(event?.title ?? "");
    setDescription(event?.description ?? "");
    setLocation(event?.location ?? "");
    setScheduledTime(toDatetimeLocal(event?.scheduled_time));
    setDuration(
      event?.duration_minutes != null ? String(event.duration_minutes) : "60"
    );
    setQrExpiry(toDatetimeLocal(event?.qr_expiry_time));
    setError("");
  }, [event]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!scheduledTime) {
      setError("Date & time is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        scheduled_time: scheduledTime
          ? new Date(scheduledTime).toISOString()
          : null,
        duration_minutes: duration !== "" ? Number(duration) : 60,
        qr_expires_at: qrExpiry ? new Date(qrExpiry).toISOString() : null,
      };

      let res;
      if (isEdit) {
        res = await attendanceService.updateAttendanceEvent(
          event.id,
          payload,
          user?.jwt
        );
      } else {
        res = await attendanceService.createAttendanceEvent(payload, user?.jwt);
      }

      if (res.success) {
        onSaved(res.data);
        onClose();
      } else {
        setError(res.message || "Failed to save event.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? "Edit Event" : "Create Attendance Event"}</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Title */}
          <div className="form-group">
            <label>
              Title <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Weekly Team Meeting"
              maxLength={150}
              autoFocus
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>
              Description{" "}
              <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                (optional)
              </span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the event…"
              rows={3}
              maxLength={500}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: 8,
                fontSize: 14,
                color: "#333",
                boxSizing: "border-box",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
              }}
              onFocus={e =>
                (e.target.style.borderColor = "var(--color-primary, #d0521f)")
              }
              onBlur={e => (e.target.style.borderColor = "#ccc")}
            />
          </div>

          {/* Location */}
          <div className="form-group">
            <label>
              Location{" "}
              <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Conference Room A"
              maxLength={200}
            />
          </div>

          {/* Date & Time */}
          <div className="form-group">
            <label>
              Date & Time <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="datetime-local"
              value={scheduledTime}
              onChange={e => setScheduledTime(e.target.value)}
            />
          </div>

          {/* Duration */}
          <div className="form-group">
            <label>Duration (minutes)</label>
            <input
              type="number"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="60"
              min={1}
              max={1440}
              step={5}
            />
          </div>

          {/* QR Expiry */}
          <div className="form-group">
            <label>
              QR Code Expiry{" "}
              <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                (optional)
              </span>
            </label>
            <input
              type="datetime-local"
              value={qrExpiry}
              onChange={e => setQrExpiry(e.target.value)}
            />
            <p
              style={{
                fontSize: 12,
                color: "#6b7280",
                margin: "4px 0 0",
              }}
            >
              Leave blank to use the event end time as expiry.
            </p>
          </div>

          {error && (
            <p style={{ color: "#ef4444", fontSize: 13, margin: "4px 0 0" }}>
              {error}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!title.trim() || !scheduledTime || saving}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Event"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;
